import { useEffect } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { useAttention } from '@/hooks/useAttention';

/**
 * Once per browser session, after the first live counts arrive, tell the user
 * what needs them. Only items that are wrong or waiting on the user qualify
 * (critical/high risks and signals, pending decisions, AI outputs awaiting
 * review, overdue and blocked work). Unread mail, new meetings and finished
 * milestones are not announced; they are information, not action. Nothing is
 * shown when there is nothing to act on.
 */
export function useLoginSummary(onNavigate: (view: string) => void) {
  const { user } = useAuth();
  const { items, isLoading } = useAttention();

  useEffect(() => {
    if (!user || isLoading) return;
    const key = `masira.login-summary.${user.id}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch { /* storage unavailable: show once per mount instead */ }
    if (items.length === 0) return;

    const top = items[0];
    const rest = items.slice(1, 4).map((i) => i.title).join(' · ');
    const show = top.priority === 'critical' ? toast.error : toast.warning;
    show(top.title, {
      description: rest || top.body,
      duration: 12_000,
      action: { label: 'Review', onClick: () => onNavigate(top.view) },
    });
    // Runs once when the first counts resolve; later refreshes use the bell.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isLoading]);
}
